import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Upload, FileText, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  tipo: "uscita", importo: "", data: todayISO(), scadenza: "", cantiere_id: "generale", fornitore_id: "nessuno",
  categoria_key: "", descrizione: "", note: "", allegato_nome: "", allegato_file_id: "", allegato_content_type: "",
};

export default function TransactionModal({ open, onOpenChange, categories, cantieri, fornitori, initial, lockCantiereId, onSaved }) {
  const { user } = useAuth();
  const canEntrata = user?.role === "owner";
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY, ...initial,
          importo: String(initial.importo ?? ""),
          cantiere_id: initial.cantiere_id || "generale",
          fornitore_id: initial.fornitore_id || "nessuno",
          allegato_file_id: initial.allegato_file_id || "",
          allegato_content_type: initial.allegato_content_type || "",
        });
      } else {
        setForm({ ...EMPTY, cantiere_id: lockCantiereId || "generale" });
      }
    }
  }, [open, initial, lockCantiereId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const catOptions = form.tipo === "entrata" ? categories?.entrate || [] : categories?.uscite || [];

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, allegato_file_id: res.file_id, allegato_nome: res.filename, allegato_content_type: res.content_type }));
      toast.success("Allegato caricato con successo.");
    } catch (err) {
      toast.error(err.message || "Caricamento non riuscito.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!form.importo || Number(form.importo) <= 0) return toast.error("Inserisci un importo valido.");
    if (!form.descrizione.trim()) return toast.error("Inserisci una descrizione.");
    if (!form.categoria_key) return toast.error("Seleziona una categoria.");
    if (!form.data) return toast.error("Seleziona una data.");

    const payload = {
      tipo: form.tipo,
      importo: Number(form.importo),
      data: form.data,
      cantiere_id: form.cantiere_id === "generale" ? null : form.cantiere_id,
      fornitore_id: form.fornitore_id === "nessuno" ? null : form.fornitore_id,
      categoria_key: form.categoria_key,
      descrizione: form.descrizione.trim(),
      note: form.note?.trim() || "",
      scadenza: form.scadenza || null,
      allegato_nome: form.allegato_nome || "",
      allegato_file_id: form.allegato_file_id || "",
      allegato_content_type: form.allegato_content_type || "",
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/transactions/${initial.id}`, payload);
        toast.success("Transazione aggiornata con successo.");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transazione aggiunta con successo.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="transaction-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica Transazione" : "Nuova Transazione"}</DialogTitle>
          <DialogDescription>Registra un'entrata o un'uscita e associala a un cantiere.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {canEntrata && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="tipo-entrata-button"
              onClick={() => { set("tipo", "entrata"); set("categoria_key", ""); }}
              className={`rounded-lg border-2 py-2.5 text-sm font-semibold transition-all ${
                form.tipo === "entrata"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "border-border text-muted-foreground hover:border-emerald-300"
              }`}
            >
              Entrata
            </button>
            <button
              type="button"
              data-testid="tipo-uscita-button"
              onClick={() => { set("tipo", "uscita"); set("categoria_key", ""); }}
              className={`rounded-lg border-2 py-2.5 text-sm font-semibold transition-all ${
                form.tipo === "uscita"
                  ? "border-red-500 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                  : "border-border text-muted-foreground hover:border-red-300"
              }`}
            >
              Uscita
            </button>
          </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Importo (€)</Label>
              <Input type="number" step="0.01" min="0" data-testid="tx-importo-input"
                placeholder="0,00" value={form.importo} onChange={(e) => set("importo", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" data-testid="tx-data-input" value={form.data} onChange={(e) => set("data", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Scadenza pagamento (facoltativa)</Label>
            <Input type="date" data-testid="tx-scadenza-input" value={form.scadenza || ""} onChange={(e) => set("scadenza", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Categoria</Label>
            <Select value={form.categoria_key} onValueChange={(v) => set("categoria_key", v)}>
              <SelectTrigger data-testid="tx-categoria-select"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
              <SelectContent>
                {catOptions.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Cantiere / Commessa</Label>
            <Select value={form.cantiere_id} onValueChange={(v) => set("cantiere_id", v)} disabled={Boolean(lockCantiereId)}>
              <SelectTrigger data-testid="tx-cantiere-select"><SelectValue placeholder="Generale" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="generale">Generale (nessun cantiere)</SelectItem>
                {(cantieri || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Fornitore (facoltativo)</Label>
            <Select value={form.fornitore_id} onValueChange={(v) => set("fornitore_id", v)}>
              <SelectTrigger data-testid="tx-fornitore-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nessuno">Nessun fornitore</SelectItem>
                {(fornitori || []).map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Descrizione / Fornitore / Cliente</Label>
            <Input data-testid="tx-descrizione-input" placeholder="Es. Fornitura cemento"
              value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Note (facoltative)</Label>
            <Textarea rows={2} data-testid="tx-note-input" placeholder="Aggiungi note..."
              value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Allegato / Ricevuta (JPG, PNG, PDF)</Label>
            {form.allegato_file_id ? (
              <div className="rounded-lg border border-border bg-secondary p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <a href={fileUrl(form.allegato_file_id)} target="_blank" rel="noreferrer" data-testid="tx-allegato-link"
                    className="flex items-center gap-2 truncate text-amber-600 hover:underline">
                    <FileText className="h-4 w-4 shrink-0" />{form.allegato_nome}
                  </a>
                  <button type="button" data-testid="tx-allegato-remove"
                    onClick={() => setForm((f) => ({ ...f, allegato_file_id: "", allegato_nome: "", allegato_content_type: "" }))}
                    className="text-muted-foreground hover:text-destructive shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {form.allegato_content_type?.startsWith("image/") && (
                  <img src={fileUrl(form.allegato_file_id)} alt="Anteprima ricevuta" className="max-h-44 rounded-md border border-border object-contain" />
                )}
              </div>
            ) : (
              <label data-testid="tx-allegato-dropzone"
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-4 text-sm text-muted-foreground hover:border-amber-400 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Caricamento in corso..." : "Carica foto o PDF della ricevuta"}
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleFile} />
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tx-cancel-button">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="tx-save-button"
            className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
